/**
 * Haptic feedback utility for mobile devices.
 * Uses Capacitor Haptics plugin on native platforms (iOS/Android),
 * falls back to the Vibration API on web.
 */
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

type HapticStyle = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';

const isNative = Capacitor.isNativePlatform();

// Web fallback patterns (milliseconds)
const VIBRATION_PATTERNS: Record<HapticStyle, number | number[]> = {
  light: 10,
  medium: 20,
  heavy: 30,
  success: [10, 50, 10],
  warning: [20, 40, 20],
  error: [30, 50, 30, 50, 30],
  selection: 5,
};

/**
 * Check if haptic feedback is available (web fallback)
 */
function isVibrationAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
}

/**
 * Native haptic feedback via Capacitor
 */
async function nativeHaptic(style: HapticStyle): Promise<void> {
  switch (style) {
    case 'light':
      await Haptics.impact({ style: ImpactStyle.Light });
      break;
    case 'medium':
      await Haptics.impact({ style: ImpactStyle.Medium });
      break;
    case 'heavy':
      await Haptics.impact({ style: ImpactStyle.Heavy });
      break;
    case 'success':
      await Haptics.notification({ type: NotificationType.Success });
      break;
    case 'warning':
      await Haptics.notification({ type: NotificationType.Warning });
      break;
    case 'error':
      await Haptics.notification({ type: NotificationType.Error });
      break;
    case 'selection':
      await Haptics.selectionStart();
      await Haptics.selectionEnd();
      break;
  }
}

/**
 * Web fallback using Vibration API
 */
function webHaptic(style: HapticStyle): void {
  if (!isVibrationAvailable()) return;
  const pattern = VIBRATION_PATTERNS[style];
  navigator.vibrate(pattern);
}

/**
 * Trigger haptic feedback.
 * Uses Capacitor Haptics on native, Vibration API on web.
 */
export function hapticFeedback(style: HapticStyle = 'light'): void {
  try {
    if (isNative) {
      // Fire and forget — don't await in sync callers
      nativeHaptic(style).catch(() => {});
    } else {
      webHaptic(style);
    }
  } catch {
    // Silently fail — haptics are non-critical
  }
}

/** Light tap feedback (button presses, tab changes) */
export function hapticTap(): void {
  hapticFeedback('light');
}

/** Selection feedback (checkboxes, toggles, radio buttons) */
export function hapticSelection(): void {
  hapticFeedback('selection');
}

/** Success feedback (form submit, action completed) */
export function hapticSuccess(): void {
  hapticFeedback('success');
}

/** Error/warning feedback (validation error, destructive action) */
export function hapticError(): void {
  hapticFeedback('error');
}
